import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin tile to set the response window (hours) given to every pro / vendor
 * to accept or decline a customer's confirmation offer. Clamped 1h-168h
 * (1 hour to 7 days) server-side.
 */
const AdminProResponseWindow = () => {
  const [hours, setHours] = useState(48);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/admin/pro-response-window`, { withCredentials: true });
        setHours(data?.pro_response_window_hours || 48);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Could not load setting');
      }
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${BACKEND_URL}/api/admin/pro-response-window`,
        { hours: Number(hours) }, { withCredentials: true });
      setHours(data.pro_response_window_hours);
      toast.success(`Response window set to ${data.pro_response_window_hours}h`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  if (loading) return null;

  return (
    <Card className="bg-white border-[#E8E1D5] mb-4" data-testid="admin-pro-response-window">
      <CardHeader className="border-b border-[#E8E1D5]">
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <Clock size={20} className="text-brand-primary" /> Pro Response Window
        </CardTitle>
        <CardDescription>
          How long every pro / vendor has to accept or decline a customer's confirmation offer before it auto-declines. Their calendar is blocked on the event date either way.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-5 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[160px]">
          <label className="text-xs uppercase tracking-wider font-bold text-text-secondary block mb-1">Hours (1–168)</label>
          <Input type="number" min={1} max={168} value={hours}
            onChange={(e) => setHours(e.target.value)}
            className="rounded-none" data-testid="admin-pro-window-input" />
        </div>
        <Button onClick={save} disabled={saving}
          className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none"
          data-testid="admin-pro-window-save">
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default AdminProResponseWindow;
